export const PILLARS = [
    {
        slug: "infor-solutions",
        name: "Infor Solutions",
        accent: "#0066FF",
        accentSoft: "rgba(0, 102, 255, 0.14)",
        icon: "Boxes",
        tagline: "Infor expertise, end to end",
        summary:
            "Implementation, integration and support across the Infor ecosystem — from WMS CloudSuite and ION to LN and Birst reporting, in the cloud or on-premises.",
        capabilities: ["Infor WMS CloudSuite", "Infor ION integrations", "Infor LN", "Birst & reporting", "Cloud & on-premises", "Go-live & ongoing support"],
        includes: [
            "Infor WMS CloudSuite implementation, configuration and optimization",
            "Infor ION integration design, development and monitoring",
            "Infor LN consulting, enhancements and support",
            "Birst analytics, dashboards and operational reporting",
            "Cloud migrations and on-premises deployments",
            "Implementation, integration, go-live and ongoing support",
        ],
        needs: [
            "Warehouse operations that have outgrown manual or paper-based processes",
            "ERP, WMS and partner systems that do not talk to each other",
            "Reports that arrive too late — or too inaccurate — to act on",
            "A planned move from on-premises Infor to the cloud",
            "An upcoming go-live that needs experienced hands around it",
        ],
        engagement: [
            { title: "Discovery & process mapping", desc: "We study your current Infor landscape, integrations and pain points before proposing anything." },
            { title: "Solution design & configuration", desc: "A right-sized design — configuration, integration points and reporting mapped to your operations." },
            { title: "Build, integrate & test", desc: "ION flows, interfaces and reports built and tested against real business scenarios." },
            { title: "Go-live & hypercare", desc: "Planned cutover with hands-on support, then steady ongoing assistance after launch." },
        ],
        image: "/assets/warehouse-modern.webp",
        imageAlt: "Modern smart warehouse with autonomous robots, glowing tracking routes and a worker using a handheld device",
    },
    {
        slug: "digital-marketing",
        name: "Digital Marketing",
        accent: "#E6007E",
        accentSoft: "rgba(230, 0, 126, 0.14)",
        icon: "Megaphone",
        tagline: "Visibility that turns into growth",
        summary:
            "Search, social, paid and email programs run on strategy and measured on performance — so every campaign has a purpose and a report to prove it.",
        capabilities: ["SEO", "Social media marketing", "Google Ads & PPC", "Email marketing", "Digital strategy", "Performance reporting"],
        includes: [
            "Search engine optimization — technical, on-page and content",
            "Social media marketing and day-to-day channel management",
            "Google Ads and pay-per-click campaign management",
            "Email marketing programs and automation",
            "Digital strategy aligned to business goals",
            "Clear, regular performance reporting",
        ],
        needs: [
            "A website that rarely shows up where customers are searching",
            "Social channels that are active but not delivering results",
            "Ad spend without a clear view of return",
            "Marketing decisions being made without reliable data",
            "A launch or expansion that needs a coordinated digital push",
        ],
        engagement: [
            { title: "Audit & goal setting", desc: "We review your current presence, analytics and competitors, then agree on measurable goals." },
            { title: "Strategy & channel plan", desc: "A practical plan across search, social, paid and email — matched to budget and capacity." },
            { title: "Execution & optimization", desc: "Campaigns and content shipped, monitored and refined continuously." },
            { title: "Reporting & next steps", desc: "Plain-language performance reports with clear recommendations for what happens next." },
        ],
        image: "https://images.unsplash.com/photo-1762278804964-9c63a61208a5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMGRpZ2l0YWwlMjBuZXR3b3JrJTIwYWklMjB0ZWNobm9sb2d5JTIwZGFya3xlbnwwfHx8fDE3OTA1MTQyNzF8MA&ixlib=rb-4.1.0&q=85",
        imageAlt: "Abstract digital network of glowing magenta and blue data streams",
    },
    {
        slug: "software-development",
        name: "Software Development",
        accent: "#7000FF",
        accentSoft: "rgba(112, 0, 255, 0.14)",
        icon: "Code2",
        tagline: "Software shaped around your business",
        summary:
            "From databases and APIs to complete web applications — custom software built on proven stacks, integrated with the systems you already run.",
        capabilities: ["SQL & PL/SQL", "Java & Python", "Web development", "API development", "Custom applications", "Technical support"],
        includes: [
            "SQL and PL/SQL database design, development and tuning",
            "Java and Python application engineering",
            "Modern, responsive web development",
            "API development and third-party integration",
            "Custom business applications built around your workflows",
            "Dependable technical support after delivery",
        ],
        needs: [
            "Manual workflows that should have been automated years ago",
            "Systems that hold data hostage instead of sharing it",
            "Off-the-shelf software that never quite fits",
            "A product idea that needs a reliable engineering team",
            "Ageing applications that need support or a careful rebuild",
        ],
        engagement: [
            { title: "Requirements & architecture", desc: "We translate business needs into a clear technical plan before writing code." },
            { title: "Iterative build", desc: "Working software in reviewable increments, so you see progress early and often." },
            { title: "Integration & hardening", desc: "APIs wired up, edge cases tested, performance and security checked." },
            { title: "Release & support", desc: "Smooth release into your environment, with technical support that stays available." },
        ],
        image: "https://images.unsplash.com/photo-1762279389006-43963a0cee55?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHszfHxhYnN0cmFjdCUyMGRpZ2l0YWwlMjBuZXR3b3JrJTIwYWklMjB0ZWNobm9sb2d5JTIwZGFyayUyMGJsdWUl2BtYWdlbnRhJTIwY3lhbnxlbnwwfHx8fDE3OTA1MTQyNzF8MA&ixlib=rb-4.1.0&q=85",
        imageAlt: "Glowing violet and cyan digital mesh representing software systems",
    },
    {
        slug: "ai-ml-generative-ai",
        name: "AI, ML & Generative AI",
        accent: "#00F0FF",
        accentSoft: "rgba(0, 240, 255, 0.12)",
        icon: "Cpu",
        tagline: "Practical AI, applied with care",
        summary:
            "AI and machine learning that solve real problems — intelligent automation, generative AI services and AI-powered tools, from first pilot to production.",
        capabilities: ["AI & ML solutions", "Generative AI services", "Intelligent automation", "AI-powered tools", "AI consulting", "Implementation"],
        includes: [
            "AI and machine learning solutions for real business problems",
            "Generative AI services — assistants, content and knowledge tools",
            "Intelligent automation of repetitive, rules-heavy work",
            "AI-powered business tools embedded in your existing systems",
            "Practical AI consulting: where AI helps, and where it does not",
            "End-to-end implementation from pilot to production",
        ],
        needs: [
            "Teams spending hours on work that machines could draft or route",
            "Data that exists but never becomes a decision",
            "Interest in generative AI without a safe place to start",
            "An AI demo that never made it into daily operations",
            "Pressure to 'do something with AI' — with a business case attached",
        ],
        engagement: [
            { title: "Use-case discovery", desc: "We identify where AI genuinely helps your business — and say so where it does not." },
            { title: "Pilot design", desc: "A small, measurable pilot with clear success criteria and safeguards." },
            { title: "Build & integrate", desc: "The solution built into your real workflows, tools and data." },
            { title: "Measure & scale", desc: "Results reviewed against the pilot criteria, then scaled deliberately." },
        ],
        image: "https://images.unsplash.com/photo-1674027444485-cec3da58eef4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxhcnRpZmljaWFsJTIwaW50ZWxsaWdlbmNlJTIwZ2xvd2luZyUyMG5ldXJhbCUyMG5ldHdvcmslMjBkYXJrfGVufDB8fHx8MTc5MDUxNDI4MHww&ixlib=rb-4.1.0&q=85",
        imageAlt: "Glowing neural network visualization representing artificial intelligence",
    },
];

export const COMMITMENTS = [
    { title: "Dedicated experts", desc: "Specialists for every service pillar, assigned to your engagement — not shared generic resources." },
    { title: "24×7 support availability", desc: "Around-the-clock support coverage, so help is available when your business needs it." },
    { title: "Post-go-live assistance", desc: "We stay involved after launch — hypercare, stabilization and steady ongoing support." },
    { title: "Cost-conscious solutions", desc: "Right-sized recommendations that respect your budget, without cutting corners on quality." },
    { title: "Planned around agreed timelines", desc: "Work scheduled against milestones we agree together — and communicated clearly throughout." },
    { title: "Customer satisfaction first", desc: "Your outcome is the measure of our work. We prioritize satisfaction on every engagement." },
];

export const REGIONS = ["India", "United States", "Middle East", "United Kingdom", "Europe", "Australia"];

export const REGION_FLAGS = {
    "India": "🇮🇳",
    "United States": "🇺🇸",
    "Middle East": "🇦🇪",
    "United Kingdom": "🇬🇧",
    "Europe": "🇪🇺",
    "Australia": "🇦🇺",
};

export const INDUSTRIES = [
    { icon: "Factory", name: "Manufacturing & Logistics", desc: "Warehousing, supply chain and ERP-driven operations that depend on systems staying in sync." },
    { icon: "HeartPulse", name: "Healthcare & Pharma", desc: "Organizations that need reliable software, clean data and careful, compliant integrations." },
    { icon: "ShoppingCart", name: "Retail & E-Commerce", desc: "Digital storefronts and operations where marketing, systems and fulfilment must work together." },
    { icon: "Landmark", name: "Financial Services", desc: "Teams that value precision — robust applications, reporting and data integrity." },
    { icon: "Zap", name: "Energy & Utilities", desc: "Asset-heavy operations looking to modernize systems and automate routine work." },
    { icon: "GraduationCap", name: "Education & Technology", desc: "Institutions and tech companies building platforms, tools and digital presence." },
];

export const SERVICE_OPTIONS = [
    { value: "infor-solutions", label: "Infor Solutions" },
    { value: "digital-marketing", label: "Digital Marketing" },
    { value: "software-development", label: "Software Development" },
    { value: "ai-ml-generative-ai", label: "AI, ML & Generative AI" },
    { value: "not-sure", label: "Not sure yet — help me decide" },
];
